/**
 * Meridian LLM provider registry — the ONE swap point for which model backend the
 * whole assistant runs on. Selected globally (all-or-nothing) by the
 * `MERIDIAN_LLM_PROVIDER` env var:
 *
 *   - `aws` (DEFAULT) — Claude Platform on AWS via `@ai-sdk/anthropic-aws`. Anthropic's
 *     Messages API hosted inside our AWS account (SigV4 + AWS Marketplace billing), which
 *     is what enables tool search (`defer_loading`) and prompt caching. Auth + workspace
 *     resolve from the AWS default credential chain and env (explicit credentials passed to
 *     avoid serverless env-merge surprises); `ANTHROPIC_AWS_WORKSPACE_ID` + `AWS_REGION`.
 *     Model IDs are BARE first-party ids (`claude-sonnet-4-6`) — no `anthropic.` prefix.
 *
 *   - `deepseek` — DeepSeek's hosted API via `@ai-sdk/deepseek` (loaded by DYNAMIC IMPORT so
 *     the AWS path never hard-requires the dependency at module load). `DEEPSEEK_API_KEY` for
 *     auth; model id from `MERIDIAN_DEEPSEEK_MODEL_ID` (default `deepseek-v4-flash`).
 *
 * Both AWS-Claude and DeepSeek are PREFIX-CACHE systems: repeated identical prompt prefixes
 * are billed/served cheaply. AWS-Claude requires EXPLICIT `cache_control: ephemeral`
 * breakpoints (applied here via `applyProviderCacheControl`); DeepSeek auto-caches identical
 * prefixes on its own disk with NO request-side headers. Either way the "stable prefix,
 * volatile tail" message-ordering discipline (tools → system → messages, datetime at the
 * tail) serves BOTH providers, so that ordering must never be broken when swapping.
 */

import { createAnthropicAws, type AnthropicAwsProvider } from '@ai-sdk/anthropic-aws';
// Type-only import (erased at compile) — the runtime `createDeepSeek` is loaded via dynamic
// import inside `resolveLlmProvider` so `@ai-sdk/deepseek` is never required on the AWS path.
import type { DeepSeekProvider } from '@ai-sdk/deepseek';
import type { AIConfig } from '../../config/config-types.js';
import type { BedrockMessage, BedrockContentBlock } from '../bedrock/message-types.js';
import { logger } from '../logger.js';
import {
  DEEPSEEK_NOT_CONFIGURED_MESSAGE,
  MeridianProviderNotConfiguredError,
  isDeepSeekConfigured,
} from './provider-config.js';

// Re-exported so `../providers` stays the ONE import site for provider concerns.
export {
  DEEPSEEK_NOT_CONFIGURED_MESSAGE,
  MeridianProviderNotConfiguredError,
  isDeepSeekConfigured,
  isProviderConfigured,
  isProviderNotConfiguredError,
} from './provider-config.js';

// `ProviderKind` + `EPHEMERAL_CACHE` live in a dependency-free LEAF module so a
// caller that needs only a cache breakpoint does not have to load this file — and
// with it `@ai-sdk/anthropic-aws`, which is not installed in every workspace. This
// barrel stays the one public import site by re-exporting them.
export { EPHEMERAL_CACHE, type ProviderKind } from './cache-control.js';
import { EPHEMERAL_CACHE, type ProviderKind } from './cache-control.js';

/**
 * Minimal callable shape common to every backend's provider factory: `provider(modelId)`
 * returns a language model. Both `AnthropicAwsProvider` and `DeepSeekProvider` are
 * structurally assignable to this (each also carries backend-specific members), so
 * single-shot callers that build a model with a bespoke id can stay backend-agnostic.
 */
export type MeridianModelFactory = (modelId: string) => ReturnType<AnthropicAwsProvider>;

/**
 * Default DeepSeek chat model when `MERIDIAN_DEEPSEEK_MODEL_ID` is unset.
 *
 * This constant is NOT decoration: `MERIDIAN_DEEPSEEK_MODEL_ID` is unset in every environment, so
 * whatever is written here is the id an env-resolved DeepSeek path actually requests. It read
 * `deepseek-chat` — the legacy alias DEPRECATED 2026-07-24 — from #2106 until #2543. That was a
 * dead model id armed in every environment at once, and it was invisible because the paths that
 * ARE exercised today carry an explicit id (the tier map and the admin model picker), and because
 * `MERIDIAN_LLM_PROVIDER` is unset → `aws`, so nothing ever asked for it.
 *
 * Keep it equal to the DeepSeek LOOKUP/STANDARD tier default in `tier-model-map.ts`
 * (`DEEPSEEK_TIER_TARGETS`) and to a real `provider: 'deepseek'` entry in `models/registry.ts` —
 * `tests/lib/deepseek-default-model.test.ts` fails if it drifts from either.
 */
const DEFAULT_DEEPSEEK_MODEL_ID = 'deepseek-v4-flash';

/** Resolved provider factory + the model built from `config.modelId`, tagged by kind. */
interface ResolvedAwsProvider {
  providerKind: 'aws';
  provider: AnthropicAwsProvider;
  model: ReturnType<AnthropicAwsProvider>;
}
interface ResolvedDeepSeekProvider {
  providerKind: 'deepseek';
  provider: DeepSeekProvider;
  model: ReturnType<DeepSeekProvider>;
}
export type ResolvedLlmProvider = ResolvedAwsProvider | ResolvedDeepSeekProvider;

/**
 * Resolve the active provider kind. The single global env `MERIDIAN_LLM_PROVIDER` is the
 * source of truth; an explicit `config.provider` (rarely set) overrides it for a call. Any
 * unrecognized value falls back to the safe `aws` default.
 *
 * Exported so the model router (`lib/router`) reads provider kind from THIS one place rather
 * than re-parsing `MERIDIAN_LLM_PROVIDER` itself — keep the env-reading logic un-duplicated.
 */
export function resolveProviderKind(config: AIConfig): ProviderKind {
  if (config.provider === 'aws' || config.provider === 'deepseek') {
    return config.provider;
  }
  const fromEnv = process.env.MERIDIAN_LLM_PROVIDER?.trim().toLowerCase();
  return fromEnv === 'deepseek' ? 'deepseek' : 'aws';
}

/**
 * Resolve the Meridian LLM provider and build the model for `config.modelId`.
 *
 * The AWS path is behaviorally identical to the prior inline `createAnthropicAws` call
 * (explicit credentials + `ANTHROPIC_AWS_WORKSPACE_ID`). The DeepSeek path is opt-in and
 * dynamically imported so the AWS path never loads `@ai-sdk/deepseek`.
 */
export async function resolveLlmProvider(config: AIConfig): Promise<ResolvedLlmProvider> {
  const providerKind = resolveProviderKind(config);

  if (providerKind === 'deepseek') {
    // ⚠️ DATA-EGRESS NOTICE (informational — this is a deliberate GLOBAL business toggle,
    // not a per-request guard): selecting `deepseek` routes ALL Meridian prompt data —
    // including any customer PII in the conversation — off AWS to DeepSeek's third-party
    // API (PRC jurisdiction). The switch is all-or-nothing for the entire assistant. The
    // default stays `aws`; flipping to `deepseek` is an operator decision that should carry
    // data-governance sign-off. Nothing here is gated in code by design.
    // FAIL FAST on a missing key. Building the provider anyway (the previous behavior)
    // only deferred the failure into the AI SDK's request headers, where it surfaced as
    // an opaque mid-stream `AI_LoadAPIKeyError` — the user saw "Something went wrong",
    // the turn saved with no assistant reply, and the picker happily kept the dead model
    // selected. This is OUR deployment misconfiguration, so it is an error (→ #Hub
    // Errors) and it carries the actionable text the chat stream shows the user.
    const apiKey = process.env.DEEPSEEK_API_KEY?.trim();
    if (!apiKey || !isDeepSeekConfigured()) {
      const notConfigured = new MeridianProviderNotConfiguredError(
        'deepseek',
        DEEPSEEK_NOT_CONFIGURED_MESSAGE,
      );
      logger.error(
        'MERIDIAN_LLM_PROVIDER=deepseek but DEEPSEEK_API_KEY is not set',
        notConfigured,
        { op: 'resolveLlmProvider', providerKind },
      );
      throw notConfigured;
    }
    const { createDeepSeek } = await import('@ai-sdk/deepseek');
    const provider = createDeepSeek({ apiKey });
    // Honor an explicit DeepSeek model id on the config (per-user model selection
    // sets one alongside `provider: 'deepseek'`). A non-DeepSeek id (e.g. a Claude
    // id left on the config when only the PROVIDER was flipped via env) is ignored
    // so the env/default id still applies — never send a Claude id to DeepSeek.
    const configModelId = config.modelId?.trim();
    const modelId =
      (configModelId?.toLowerCase().startsWith('deepseek') ? configModelId : undefined) ||
      process.env.MERIDIAN_DEEPSEEK_MODEL_ID?.trim() ||
      DEFAULT_DEEPSEEK_MODEL_ID;
    logger.info('meridian LLM provider resolved', {
      op: 'resolveLlmProvider',
      providerKind,
      modelId,
    });
    return { providerKind, provider, model: provider(modelId) };
  }

  // Default: Claude Platform on AWS. Explicit credentials avoid serverless env-merge
  // surprises; workspace + region come from env (no in-provider fallback for workspace).
  const provider = createAnthropicAws({
    region: config.region || process.env.AWS_REGION || 'us-east-1',
    workspaceId: process.env.ANTHROPIC_AWS_WORKSPACE_ID,
    accessKeyId: process.env.AWS_ACCESS_KEY_ID,
    secretAccessKey: process.env.AWS_SECRET_ACCESS_KEY,
    sessionToken: process.env.AWS_SESSION_TOKEN,
  });
  return { providerKind, provider, model: provider(config.modelId) };
}

/**
 * Place a cache breakpoint on the first user message's last content block. The prefix is
 * cumulative (tools → system → messages), so this ONE breakpoint caches tools + system + the
 * (stable, turn-anchoring) first user message — read back ITPM-free on step 2+. A string body
 * is promoted to a text block so the breakpoint has somewhere to attach. Mutates in place.
 */
function markFirstUserMessageForCache(messages: BedrockMessage[]): void {
  const firstUser = messages.find((m) => m.role === 'user');
  if (!firstUser) return;
  if (typeof firstUser.content === 'string') {
    firstUser.content = [
      { type: 'text', text: firstUser.content, providerOptions: EPHEMERAL_CACHE },
    ] as unknown as BedrockContentBlock[];
    return;
  }
  if (Array.isArray(firstUser.content) && firstUser.content.length > 0) {
    const last = firstUser.content[firstUser.content.length - 1] as Record<string, unknown>;
    const po = (last.providerOptions as { anthropic?: Record<string, unknown> } | undefined) ?? {};
    last.providerOptions = { ...po, anthropic: { ...(po.anthropic ?? {}), ...EPHEMERAL_CACHE.anthropic } };
  }
}

/**
 * Rolling cache breakpoint on the LAST message's last block — the multi-TURN pattern from the
 * Anthropic docs. The breakpoint moves forward each turn, so a long back-and-forth reads all
 * prior turns from cache and only writes the new tail. Complements the (stable) first-message
 * breakpoint, which is what an in-turn step-2 reads. Mutates in place.
 */
function markLastMessageForCache(messages: BedrockMessage[]): void {
  const lastMsg = messages[messages.length - 1];
  if (!lastMsg) return;
  if (typeof lastMsg.content === 'string') {
    lastMsg.content = [
      { type: 'text', text: lastMsg.content, providerOptions: EPHEMERAL_CACHE },
    ] as unknown as BedrockContentBlock[];
    return;
  }
  if (Array.isArray(lastMsg.content) && lastMsg.content.length > 0) {
    const last = lastMsg.content[lastMsg.content.length - 1] as Record<string, unknown>;
    const po = (last.providerOptions as { anthropic?: Record<string, unknown> } | undefined) ?? {};
    last.providerOptions = { ...po, anthropic: { ...(po.anthropic ?? {}), ...EPHEMERAL_CACHE.anthropic } };
  }
}

/** Params for {@link applyProviderCacheControl}. Messages are mutated in place. */
export interface ProviderCacheControlParams {
  /** Bedrock-shaped messages to receive cache breakpoints (AWS only). */
  messages: BedrockMessage[];
}

/**
 * Caching-parity seam. Applies the provider-appropriate prompt-cache treatment to the request
 * messages while keeping the SAME "stable prefix / volatile tail" ordering for every provider:
 *
 *   - `aws`: attach explicit `cache_control: ephemeral` breakpoints (first stable user message
 *     for in-turn step-2 reuse + rolling last message for multi-turn reuse). Cache reads are
 *     EXCLUDED from the ITPM rate limit — the main defense against 429s (see
 *     MERIDIAN_PROMPT_CACHING). Behavior is identical to the prior inline breakpoint calls.
 *   - `deepseek`: NO-OP. DeepSeek auto-caches identical prompt prefixes on disk with no
 *     request-side headers; adding Anthropic `providerOptions` would be meaningless. The
 *     prefix stability that makes those disk hits land is preserved by the unchanged message
 *     ordering upstream, so DeepSeek still benefits without any breakpoint here.
 */
export function applyProviderCacheControl(
  params: ProviderCacheControlParams,
  providerKind: ProviderKind,
): void {
  if (providerKind !== 'aws') {
    return;
  }
  markFirstUserMessageForCache(params.messages);
  markLastMessageForCache(params.messages);
}
